import { isBrowser } from './env';
import { getApiHost, addPathPrefix } from './url';
import { genTraceID } from './utils';
import { getAppUserToken } from './jsBridge';

export const getLocal = () => {
  if (!isBrowser) {
    return 'en';
  }

  let lang = window.location.pathname?.split('/')?.[1] || '';
  lang = lang.indexOf('-') ? lang : '';
  lang = lang === 'zh-CN' ? 'zh-MY' : lang;

  return lang || localStorage.getItem('BYBIT_LANG_KEY') || 'en';
};

export type Response<T = unknown> = {
  code: number;
  success: boolean;
  message: string;
  result: T;
};

type CommonData = { [index: string]: any };

type ReqInterceptor = (
  reqData: CommonData,
  reqHeaders: CommonData
) => CommonData | Promise<CommonData>;

// 请求拦截器
const reqInterceptors: ReqInterceptor[] = [];

export const addReqInterceptor = (reqInterceptor: ReqInterceptor) => {
  reqInterceptors.push(reqInterceptor);
};

export const removeReqInterceptor = (reqInterceptor: ReqInterceptor) => {
  const index = reqInterceptors.findIndex((item) => item === reqInterceptor);

  if (index !== -1) {
    reqInterceptors.splice(index, 1);
  }
};

// 去除请求参数中值为undefined的key
addReqInterceptor((reqData) => {
  for (const key in reqData) {
    if (reqData[key] === undefined) {
      delete reqData[key];
    }
  }
  return reqData;
});

export async function request(
  method: 'GET' | 'POST',
  url: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  reqData?: CommonData,
  reqHeaders?: CommonData
) {
  for (const reqInterceptor of reqInterceptors) {
    reqData = await reqInterceptor(reqData, reqHeaders);
  }

  const params =
    method === 'GET' && reqData ? new URLSearchParams(reqData).toString() : '';

  const headers = {
    'Content-Type': 'application/json',
    // For account blocking
    'Accept-Language': getLocal(),
    ...reqHeaders,
  } as CommonData;

  const token = await getAppUserToken();
  if (token) {
    headers.usertoken = token;
  }

  // 非节点请求添加其他请求头参数
  if (!url?.includes('/web3/node/') && !url?.includes('/apex/api/')) {
    headers.traceparent = genTraceID();
  }

  const response = await fetch(`${url}${params ? `?${params}` : ''}`, {
    method,
    credentials: 'include',
    headers,
    body: method === 'POST' && reqData ? JSON.stringify(reqData) : undefined,
  });
  const data = await response.json();

  const retCode = data.retCode ?? data.ret_code;

  let formatResponse: Response = {
    code: 0,
    success: true,
    message: '',
    result: '',
  };

  const isSuccess = retCode === 0 || !!data.entries;
  formatResponse = {
    code: retCode as number,
    success: isSuccess,
    message: (data.ret_msg || data.retMsg || data.error) as string,
    result: data.result || data.entries,
  };

  if (!isSuccess && !formatResponse.message) {
    formatResponse.message = (data.error_message as string) || '';
  }

  return formatResponse;
}

export async function getResultFromResponse<T>(
  item: Promise<Response<T>> | Response<T>,
  onError?: (res: Response<T>) => void
) {
  const res = await item;
  if (res.success) {
    return res.result;
  }
  if (onError) {
    onError(res);
  }
  return Promise.reject(res);
}

export const get = async <T>(
  url: string,
  data?: CommonData,
  headers?: CommonData
): Promise<Response<T>> => {
  const res = (await request(
    'GET',
    `${getApiHost()}${addPathPrefix(url)}`,
    data,
    headers
  )) as Response<T>;
  return res;
};

export const post = async <T>(
  url: string,
  data?: CommonData,
  headers?: CommonData
): Promise<Response<T>> => {
  const res = (await request(
    'POST',
    `${getApiHost()}${addPathPrefix(url)}`,
    data,
    headers
  )) as Response<T>;
  return res;
};
